import PropTypes from 'prop-types'
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import { isTouch } from 'lib/support'
import classNames from 'lib/util/classNames'
import getUniqueIdString from 'lib/util/uniq'
import Tooltip from '../../Tooltip'
import tooltipStyles from '../../Tooltip.module.scss'
import { useTooltipContext } from '../../provider/TooltipProvider'
import styles from './Target.module.scss'
/**
 * The `Tooltip` component
 *
 * @returns {React.ReactElement} the element
 */
const Target = forwardRef((props, forwardedRef) => {
  const { Tag, children, className, tooltipContent, tooltipMode, ...rest } =
    props
  const ref = useFallbackRef(forwardedRef)
  const uid = useMemo(() => getUniqueIdString('tooltip-'), [])
  const { hideTooltip, showTooltip, tooltipId } = useTooltipContext()
  const [isShowTooltip, setIsShowTooltip] = useState(false)
  const currentIsShowTooltip = useRef(false)
  const intersectionObserver = useRef()
  const classNameOutput = useMemo(
    () => classNames([className, styles.container]),
    [className]
  )

  /**
   * Handle pointer enter
   */
  const handlePointerEnter = useCallback(e => {
    setIsShowTooltip(true)

    showTooltip(
      {
        clientX: e.clientX,
        clientY: e.clientY
      },
      uid
    )
  }, [])

  /**
   * Handle pointer leave
   */
  const handlePointerLeave = useCallback(() => {
    setIsShowTooltip(false)
    hideTooltip()
  }, [])

  /**
   * Handle click
   */
  const handleClick = useCallback(
    e => {
      if (tooltipMode === 'button') {
        const isShow = !currentIsShowTooltip.current

        setIsShowTooltip(isShow)

        if (isShow) {
          showTooltip(
            {
              target: ref.current
            },
            uid
          )
        } else {
          hideTooltip()
        }

        return
      }

      handlePointerLeave()
    },
    [tooltipMode]
  )

  /**
   * Handle intersections
   */
  const handleIntersection = useCallback(entries => {
    const isIntersecting = entries.some(({ isIntersecting }) => isIntersecting)

    if (!isIntersecting) {
      // No longer intersecting, remove
      handlePointerLeave()
    }
  }, [])

  /**
   * Click anywhere (when open, to close it)
   */
  const handleClickAnywhere = useCallback(e => {
    if (ref.current.contains(e.target) || e.target === ref.current) {
      // Defensive
      return
    }

    if (tooltipMode === 'button') {
      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A') {
        setIsShowTooltip(false)

        return
      }

      // It's a button tooltip, look for a tooltip node
      if (e.target.classList.contains(tooltipStyles.tooltip)) {
        return
      }

      let parentElement = e.target.parentElement

      while (parentElement) {
        if (parentElement.classList.contains(tooltipStyles.tooltip)) {
          return
        }

        parentElement = parentElement.parentElement
      }
    }

    setIsShowTooltip(false)
  }, [])

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    if (tooltipMode === 'button') {
      ref.current.addEventListener('click', handleClick)

      return () => {
        if (!ref.current) {
          return
        }

        ref.current.removeEventListener('mouseenter', handlePointerEnter)
        ref.current.removeEventListener('mouseleave', handlePointerLeave)
        ref.current.removeEventListener('click', handleClick)
      }
    }

    if (isTouch) {
      // Disable for touch
      return
    }

    ref.current.addEventListener('mouseenter', handlePointerEnter)
    ref.current.addEventListener('mouseleave', handlePointerLeave)
    ref.current.addEventListener('click', handleClick)

    return () => {
      if (!ref.current) {
        return
      }

      ref.current.removeEventListener('mouseenter', handlePointerEnter)
      ref.current.removeEventListener('mouseleave', handlePointerLeave)
      ref.current.removeEventListener('click', handleClick)
    }
  }, [tooltipMode])

  /**
   * Effect: When the `isShowTooltip` changes
   */
  useEffect(() => {
    currentIsShowTooltip.current = isShowTooltip

    if (isShowTooltip) {
      document.addEventListener('click', handleClickAnywhere)

      intersectionObserver.current = new IntersectionObserver(
        handleIntersection
      )

      intersectionObserver.current.observe(ref.current)
    } else {
      document.removeEventListener('click', handleClickAnywhere)

      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect()
      }
    }

    return () => {
      document.removeEventListener('click', handleClickAnywhere)

      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect()
      }
    }
  }, [isShowTooltip])

  /**
   * Effect: When the active tooltip ID changes, and is
   * not the current tooltip, hide it (avoids double tooltips)
   */
  useEffect(() => {
    if (tooltipId !== uid) {
      setIsShowTooltip(false)
    }
  }, [tooltipId])

  /**
   * Effect: On unmount, cleanup
   */
  useEffect(
    () => () => {
      if (currentIsShowTooltip.current) {
        hideTooltip()
      }
    },
    []
  )

  return (
    <Tag ref={ref} className={classNameOutput} {...rest} aria-describedby={uid}>
      {children}
      {isShowTooltip && <Tooltip.Portal>{tooltipContent}</Tooltip.Portal>}
    </Tag>
  )
})

// Display name
Target.displayName = 'Target'

/** @type {object} */
Target.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  tooltipContent: PropTypes.element,
  Tag: PropTypes.elementType,
  tooltipMode: PropTypes.string
}

/** @type {object} */
Target.defaultProps = {
  children: [],
  className: '',
  tooltipContent: null,
  Tag: 'div',
  tooltipMode: 'standard'
}

// Export
export default memo(Target)
